import { AllowedUserActionItem, CartItem } from '@teko/cart/v2';
import {
  BenefitItems,
  DiscoveryAttribute,
  DiscoveryFlashSale,
  DiscoveryPrice,
  DiscoveryProduct,
  DiscoveryPromotion,
  ProductDetailImage,
  ProductDetailProductGroup,
  ProductDetailProductGroupConfiguration,
  ProductDetailProductGroupVariant,
  ProductDetailProductGroupVariantAttrValue,
  ProductStatus,
  PromotionWithPrice,
} from '@teko/discovery';
import {
  ARRAY_OF_NUMBER_PAYLOAD_FILTER,
  ARRAY_OF_STRING_PAYLOAD_FILTER,
  CategoriesFilter,
  ORDER_PAYLOAD_SORTING,
  OrderBy,
  PRIMITIVE_PAYLOAD_FILTER,
  SearchProduct,
  SearchProductFilter,
  SortBy,
} from '@teko/discovery/v2';
import { Button, Caption, TagProps, Tooltip, omit } from '@teko/ds-desktop';
import { EditOutlined } from '@teko/ds-icon';
import { MoneyPromotion, ProductPromotion } from '@teko/ecommerce';
import { ProductPriceProps as MobileProductPriceProps } from '@teko/ecommerce/mobile';
import { TrackAddToCartParams, trackCustomEvent } from '@teko/tracking';
import {
  AddToCartProps,
  CategoriesFilterOption,
  Filter,
  ProductCardProps as NewProductCardProps,
  ProductCardButton,
  ProductCardProps,
  ProductPriceProps,
  ProductTag,
} from '@teko/types/ecommerce';
import { cleanObject } from '@teko/utils';
import { ITEM_PER_PAGE_IN_LISTING_PAGE } from '../constants';
import { PRODUCT_DETAIL_PAGE } from '../contexts/ScreenContext';
import { t } from '../i18n';
import {
  ConvertedConfiguration,
  ConvertedProduct,
  ObjectMap,
  OptionConvertProductCart,
  PromotionApplyOnEnum,
  TenantConfig,
} from '../types';
import { checkExpiryDateTimestamp } from './common';
import { isAdultCategoryProduct } from './confirm18YearsOld';
import { isServerless } from './index';
import { getFrame, isVariantUrlGrouped } from './products';

const isExpired = (endTimestampSec: string): boolean => {
  const end = new Date(parseInt(endTimestampSec) * 1000).getTime();
  const now = new Date().getTime();
  return end < now;
};

export const convertGift = (gift: BenefitItems) => {
  return {
    ...gift,
    image: {
      alt: gift.name,
      src: gift.imageUrl || `/api/products/${gift.sku}/default_image`,
    },
  };
};

const getRemainingTime = (endDate: string | number): string | undefined => {
  const remainingTime =
    (new Date(endDate).valueOf() - new Date().valueOf()) / 86400000;
  if (remainingTime < 6 && remainingTime >= 0) {
    if (remainingTime >= 1) {
      return `${Math.floor(remainingTime)} ${t('ngày')}`;
    } else {
      const hours = Math.floor(
        (new Date(endDate).valueOf() - new Date().valueOf()) / 3600000,
      );
      if (hours >= 1) {
        return `${hours} ${t('giờ')}`;
      } else {
        return `1 ${t('giờ')}`;
      }
    }
  }
  return undefined;
};
export const convertCommonPromotion = (
  promotion: DiscoveryPromotion,
  price: number,
) => {
  let milestoneMoney = price;
  const promo: ProductPromotion = {
    id: promotion.id,
    endedAt: new Date(parseInt(promotion.endTimestampSec) * 1000).toISOString(),
    description: promotion.description,
    name: promotion.name,
    remainingTime: getRemainingTime(parseInt(promotion.endTimestampSec) * 1000),
    type: promotion?.condition?.coupon
      ? 'coupon'
      : promotion.applyOn === 'product'
      ? 'product'
      : 'order',
    condition: {
      orderValueMax: promotion.condition.orderValueMax,
      orderValueMin: promotion.condition.orderValueMin,
      coupon: promotion.condition.coupon,
      blockSize: promotion.condition.blockSize,
      minQuantity: promotion.condition.minQuantity,
      blockSizeDecimal: promotion.condition.blockSizeDecimal || 0,
      minQuantityDecimal: promotion.condition.minQuantityDecimal || 0,
    },
    benefit: {
      money: [],
      items: [],
      voucher: promotion?.benefit?.voucher,
    },
  };
  const gifts = promotion.benefit.gifts;
  if (gifts && Array.isArray(gifts)) {
    promo.benefit.items = gifts.map(convertGift);
  }
  const discount = promotion.benefit.discount;
  if (discount) {
    let discountMoney = 0;
    if (discount.percent) {
      if ((milestoneMoney * discount.percent) / 100 > discount.maxAmount) {
        discountMoney = discount.maxAmount;
      } else {
        discountMoney = (milestoneMoney * discount.percent) / 100;
      }
    } else {
      discountMoney = discount.flat;
    }
    const itemMoney: MoneyPromotion = {
      discountType: discount.percent ? 'percent' : 'money',
      maxDiscount: discount.maxAmount,
      id: promotion.id,
      percent: discount.percent,
      money: Math.round(discountMoney),
    };
    milestoneMoney = milestoneMoney - discountMoney;
    promo.benefit.money = [itemMoney];
  }
  promo.price = promotion?.price?.sellPrice
    ? +promotion?.price?.sellPrice
    : milestoneMoney;
  return { promotion: promo, price: milestoneMoney };
};

const convertProductStatus = (
  status: ProductStatus,
):
  | {
      status: string;
      color: string;
    }
  | undefined => {
  switch (status?.sellingCode) {
    case 'hang_trung_bay':
      return { status: t('Hàng trưng bày'), color: '#b4b4b4' };
    case 'hang_khac':
      return { status: t('Hàng quà tặng'), color: '#b4b4b4' };
    default:
      return undefined;
  }
};

const sortVariant = (
  variants: ProductDetailProductGroupVariant[],
  configurations: ProductDetailProductGroupConfiguration[],
): ProductDetailProductGroupVariant[] => {
  if (!variants || !configurations) {
    return [];
  }
  const configurationCodes = configurations.map((obj) => obj.code);
  const sortedVariants: ProductDetailProductGroupVariant[] = [];
  variants.forEach((variant) => {
    const temp: ProductDetailProductGroupVariant = {
      sku: variant.sku,
      attributeValues: [],
    };
    configurationCodes.forEach((code) => {
      const tmp = variant.attributeValues.findIndex(
        (att: ProductDetailProductGroupVariantAttrValue) => att.code === code,
      );
      if (tmp !== -1) {
        temp.attributeValues.push(variant.attributeValues[tmp]);
      }
    });
    sortedVariants.push(temp);
  });
  return sortedVariants;
};

const convertConfigurations = (
  variants: ProductDetailProductGroupVariant[],
  configurations: ProductDetailProductGroupConfiguration[],
  sku: string,
): ConvertedConfiguration[] => {
  if (!variants || !configurations) return [];
  const newVariants: ProductDetailProductGroupVariantAttrValue[] = [];
  const newConfigurations: ConvertedConfiguration[] = [];
  variants.forEach((variant) => {
    (variant.attributeValues || []).forEach((attributeValue) => {
      newVariants.push(attributeValue);
    });
  });
  const productVariant = variants.find((variant) => variant.sku === sku);
  configurations.forEach((item) => {
    const optionsTmp: ConvertedConfiguration['options'] = [];
    (item.options || []).forEach((option) => {
      const tmp = newVariants.findIndex(
        (variant) => option.optionId === variant.optionId,
      );
      if (tmp !== -1) {
        const defaultVariant =
          productVariant &&
          productVariant.attributeValues.findIndex(
            (att) => att.optionId === option.optionId,
          );
        optionsTmp.push({
          value: option.value,
          optionId: option.optionId,
          visible: true,
          selected: defaultVariant !== -1,
          thumbnailUrl: option.thumbnailUrl || '',
        });
      }
    });

    // simple sort by values
    const sortedOptionsTmp = optionsTmp.sort((a, b) =>
      a.value < b.value ? -1 : 1,
    );

    newConfigurations.push({
      ...item,
      options: sortedOptionsTmp,
    });
  });
  return newConfigurations;
};
const validateVisibleVariant = (
  selectedVariant: ProductDetailProductGroupVariantAttrValue[],
  variants: ProductDetailProductGroupVariant[],
  configurations: ConvertedConfiguration[],
): ConvertedConfiguration[] => {
  if (
    !selectedVariant?.length ||
    !variants?.length ||
    !configurations?.length
  ) {
    return [];
  }
  variants.forEach((variant) => {
    if (
      variant?.attributeValues?.[0] &&
      variant.attributeValues[0].optionId === selectedVariant[0].optionId
    ) {
      let count = 0;
      variant.attributeValues.forEach((att, index: number) => {
        if (att.optionId === selectedVariant[index]?.optionId) {
          count++;
        }
      });
      if (count >= variant.attributeValues.length - 1) {
        variant.attributeValues.forEach((att, index: number) => {
          const temp = configurations[index].options.find(
            (config) => config.optionId === att.optionId,
          );
          if (temp && !temp.visible) {
            const pos = configurations[index].options.findIndex(
              (config) => config.optionId === att.optionId,
            );
            if (pos !== -1)
              configurations[index].options.splice(pos, 1, {
                ...temp,
                visible: true,
              });
          }
        });
      }
    }
  });
  return configurations;
};
const convertFlashSale = (
  flashSales: DiscoveryFlashSale[],
): ConvertedProduct['flashSale'] => {
  const flashSale =
    !!flashSales.length &&
    flashSales.find(
      (fls) =>
        checkExpiryDateTimestamp(
          fls?.startTimestampSec,
          fls?.endTimestampSec,
        ) &&
        (fls?.type === 'HOT_DEAL' || fls?.type === 'GOLDEN_TIME'),
    );
  if (flashSale)
    return {
      total: flashSale.totalCount,
      used: flashSale.usedCount,
      endAt: new Date(parseInt(flashSale.endTimestampSec) * 1000).toISOString(),
      type: flashSale.type,
      endTimestampSec: flashSale.endTimestampSec,
    };
  return undefined;
};

const getDefaultPromotions = (
  product: DiscoveryProduct,
): DiscoveryPromotion[] => {
  const couponProductPromotion = (product.promotions || []).filter((promo) => {
    const isTypeCoupon = promo.type === 'coupon';
    const isApplyOnProduct = promo.applyOn === PromotionApplyOnEnum.product;
    const hasCoupon = !!promo.condition.coupon;
    return isTypeCoupon && isApplyOnProduct && hasCoupon;
  })?.[0];
  const defaultPromotions = (product.promotions || []).filter((promo) => {
    const isTypeProduct = promo.type === 'product';
    const isApplyOnProduct = promo.applyOn === PromotionApplyOnEnum.product;
    const isDefault = promo.isDefault;
    return isTypeProduct && isApplyOnProduct && isDefault;
  });
  return [...defaultPromotions, couponProductPromotion]
    .filter(Boolean)
    .filter((promo) => {
      if (promo.endTimestampSec) {
        return !isExpired(promo.endTimestampSec);
      }
      return true;
    });
};

const getSelectionPromotions = (
  product: DiscoveryProduct,
): DiscoveryPromotion[] => {
  // blockSizeDecimal and rounding float number
  // only divide when blockSizeDecimal is between 0 and 1 (not include 0 and 1)
  const promotions = (product.promotions || []).map((el) => {
    const blockSizeDecimal = el?.condition?.blockSizeDecimal;
    if (blockSizeDecimal === 0 || !blockSizeDecimal || blockSizeDecimal >= 1)
      return el;
    const discount = el?.benefit?.discount;
    let flat = discount?.flat;
    if (flat) {
      flat = flat / blockSizeDecimal;
    }
    return {
      ...el,
      benefit: {
        ...el.benefit,
        discount: discount && {
          ...discount,
          flat,
        },
      },
    };
  });
  return promotions
    .filter((promo) => {
      const isTypeProduct = promo.type === 'product';
      const isApplyOnProduct = promo.applyOn === PromotionApplyOnEnum.product;
      const isDefault = promo.isDefault;
      return isTypeProduct && isApplyOnProduct && !isDefault;
    })
    .filter((promo) => {
      if (promo.endTimestampSec) {
        return !isExpired(promo.endTimestampSec);
      }
      return true;
    });
};

const getImagesFromProduct = (
  product: DiscoveryProduct,
): ConvertedProduct['images'] => {
  if (!!product?.productDetail?.images?.length) {
    return product.productDetail.images.map((image: ProductDetailImage) => {
      return {
        src: image.url || '',
        alt: image.label || product.productInfo?.displayName || '',
      };
    });
  }
  if (!!product.productInfo.imageUrl) {
    return [
      {
        src: product.productInfo.imageUrl,
        alt: product.productInfo?.displayName || '',
      },
    ];
  }
  return [];
};

const getVideosFromProduct = (
  product: DiscoveryProduct,
): ConvertedProduct['videos'] => {
  if (!!product?.productDetail?.videos?.length) {
    return product.productDetail.videos;
  }

  return [];
};

//#region
export const convertProductDetail = (
  product: DiscoveryProduct,
): ConvertedProduct => {
  const status = convertProductStatus(
    product?.status || { sellingCode: '', sellable: false },
  );
  const productGroup = (product.productDetail.productGroup ||
    {}) as ProductDetailProductGroup;
  const variants = productGroup.variants || [];
  const configurations = productGroup.configurations || [];
  const sortedVariants = sortVariant(variants, configurations);
  const selected =
    sortedVariants.find((variant) => variant.sku === product.productInfo.sku)
      ?.attributeValues || [];
  const convertedConfiguration =
    sortedVariants &&
    convertConfigurations(variants, configurations, product.productInfo.sku);
  const validatedConfiguration =
    convertedConfiguration &&
    validateVisibleVariant(selected, sortedVariants, convertedConfiguration);
  let selectionPromotions = product.selectionPromotions || [];
  if (!product?.selectionPromotions?.length) {
    selectionPromotions = product.promotions.filter(
      (el) =>
        !el.isDefault && el.applyOn === 'product' && !el?.condition?.coupon,
    ) as PromotionWithPrice[];
  }

  const attributes: DiscoveryAttribute[] = [];

  const newProduct = {
    sku: product.productInfo.sku,
    sellerSku: product.productInfo.sellerSku,
    flashSale: convertFlashSale(product.flashSales || []),
    status: product.status,
    images: getImagesFromProduct(product),
    videos: getVideosFromProduct(product),
    title: {
      name: product.productInfo.displayName || product.productInfo.name,
      ...(status && {
        status: {
          value: status.status,
          color: status.color,
        },
      }),
    },
    cookingRecipes: product.cookingRecipes,
    name: product.productInfo.displayName || product.productInfo.name,
    displayName: product.productInfo.displayName || product.productInfo.name,
    price: {
      terminalPrice: product?.prices?.[0]?.terminalPrice,
      supplierRetailPrice: product?.prices?.[0]?.supplierRetailPrice,
      sellPrice: product?.prices?.[0]?.sellPrice,
      priceWithoutSelectionPromotion: product.priceWithoutSelectionPromotion,
      discountAmount: +product?.prices?.[0]?.discountAmount,
      ...convertPrice(product),
    },
    seoInfo: product?.productDetail?.seoInfo,
    brand: product.productInfo.brand,
    brands: product.productInfo.brands || [],
    breadcrumbs: product.productInfo.breadcrumbs?.map((item) => ({
      name: item.label,
      link: item.url,
    })),
    attributeGroups: product.productDetail.attributeGroups,
    attributes,
    needManageStock: product.totalAvailable !== null,
    totalAvailable: product.totalAvailable,
    selectionPromotions:
      product.selectionPromotions || getSelectionPromotions(product),
    categories: product.productInfo.categories,
    masterCategories: product.productInfo.masterCategories,
    attributeSet: product.productDetail.attributeSet,
    defaultPromotions:
      product.defaultPromotions || getDefaultPromotions(product),
    productGroup: !!product.productDetail.productGroup
      ? {
          ...product.productDetail.productGroup,
          variants: sortedVariants || [],
          configurations: validatedConfiguration || [],
        }
      : undefined,
    orderCouponPromotions: (product.promotions || []).filter(
      (el) => el.applyOn === 'order' && !el.isDefault,
    ),
    partOrderPromotions: (product.promotions || []).filter(
      (el) =>
        el.applyOn === 'part_order' && el.isDefault && !!el.condition.coupon,
    ),
    provider: product.provider,
    seller: product.productInfo.seller,
    selected,
    isBundle: false,
    tags: product.productInfo.tags,
    save: Number(product?.prices?.[0]?.discountAmount) || 0,
    saveWithoutSelectionPromotion: product.priceWithoutSelectionPromotion
      ? Number(product?.prices?.[0]?.supplierRetailPrice || 0) -
        product.priceWithoutSelectionPromotion
      : Number(product?.prices?.[0]?.discountAmount),
    selectPromotionId: selectionPromotions?.[0]?.id,
    isAlcoholicCategory: product.isAlcoholicCategory,
    uomName: product.productInfo.uomName,
    uomRatio: product.productInfo.uomRatio,
    uomCode: product.productInfo.uomCode,
    clearanceTypeDescription: product?.productDetail?.clearanceTypeDescription,
    productOptions: product.productOptions,
    barcode: product.productInfo.barcode,
  };
  if (newProduct.price.price === newProduct.price.listedPrice) {
    newProduct.price.listedPrice = undefined;
  }
  return newProduct;
};
///#endregion

const isValidPrice = (product: DiscoveryProduct) => {
  const sellPrice = product?.prices?.[0]?.sellPrice;
  const latestPrice = product?.prices?.[0]?.latestPrice;
  if (sellPrice === null || latestPrice === null) return false;
  const sellPriceNumber = Number(sellPrice);
  const latestPriceNumber = Number(latestPrice);
  if (isNaN(sellPriceNumber) || isNaN(latestPriceNumber)) return false;
  return sellPriceNumber > 0 && latestPriceNumber >= 0;
};

const getDisplayPrice = (
  discoveryPrice: DiscoveryPrice,
  isFlashSale: boolean,
): ProductPriceProps => {
  let priceRange: ProductPriceProps['priceRange'];
  const minLatestPrice = parseFloat(discoveryPrice.minLatestPrice);
  const maxLatestPrice = parseFloat(discoveryPrice.maxLatestPrice);
  const latestPrice = parseFloat(discoveryPrice.latestPrice);

  if (!isNaN(minLatestPrice) && !isNaN(maxLatestPrice) && !isNaN(latestPrice)) {
    if (
      minLatestPrice !== 0 &&
      maxLatestPrice !== 0 &&
      (minLatestPrice !== latestPrice || maxLatestPrice !== latestPrice)
    ) {
      priceRange = {
        min: minLatestPrice,
        max: maxLatestPrice,
      };
    }
  }

  return cleanObject({
    latestPrice: +discoveryPrice.latestPrice,
    supplierRetailPrice: discoveryPrice.discountPercent
      ? +discoveryPrice.supplierRetailPrice
      : undefined,
    discountPercent:
      discoveryPrice.discountPercent && !isFlashSale
        ? `-${discoveryPrice.discountPercent}%`
        : undefined,
    discountAmount:
      +discoveryPrice.discountAmount > 0 && !isFlashSale
        ? +discoveryPrice.discountAmount
        : undefined,
    priceRange,
  });
};

export class ProductHelper {
  milestoneShippingFee?: number;
  constructor(milestoneShippingFee?: number) {
    this.milestoneShippingFee = milestoneShippingFee;
  }

  hasGift(promotions: DiscoveryPromotion[]): boolean {
    let items: BenefitItems[] = [];
    promotions.forEach((promotion) => {
      if (Array.isArray(promotion.benefit.gifts)) {
        items = items.concat(promotion?.benefit?.gifts || []);
      }
    });
    return !!items.length;
  }
  convertProductDetail(product: DiscoveryProduct): ConvertedProduct {
    return convertProductDetail(product);
  }

  convertNewProductCard = (
    product: DiscoveryProduct,
    options?: OptionConvertProductCart,
  ): NewProductCardProps => {
    const {
      productInfo,
      promotions,
      totalAvailable,
      prices,
      flashSales,
      status,
      pos365StockInfo,
      productDetail,
    } = product;
    const {
      frames,
      cartV2,
      showUom,
      contactLink,
      showCtaButton,
      showNewProductGift,
      wishlistButton,
      bestComboDiscount,
      benefitLabel,
    } = options || {};

    const gifts = [];

    let hasGift = false;
    let freeship = false;
    const productPrice = prices.length ? prices?.[0] : undefined;
    // Hiển thị icon freeship nếu đơn hàng lớn hơn hoặc bằng milestoneShippingFee
    if (
      typeof this.milestoneShippingFee === 'number' &&
      this.milestoneShippingFee >= 0 &&
      productPrice?.latestPrice &&
      Number(productPrice?.latestPrice) >= this.milestoneShippingFee
    )
      freeship = true;
    promotions.forEach((el) => {
      if (
        el.isDefault &&
        el.applyOn === PromotionApplyOnEnum.product &&
        (el?.benefit?.gifts || []).length
      ) {
        hasGift = true;
        gifts.push(
          ...(el?.benefit?.gifts || []).map((item) => ({
            src: item.imageUrl,
            alt: item.name,
            sku: item.sku,
            quantity: item.quantity,
          })),
        );
      }
    });
    const optionPromotions = promotions.filter(
      (el) => !el.isDefault && el.applyOn === PromotionApplyOnEnum.product,
    );

    const selectPromotionId = optionPromotions[0]?.id;

    if (
      optionPromotions.length &&
      optionPromotions[0] &&
      optionPromotions[0].benefit?.gifts.length
    ) {
      hasGift = true;
      gifts.push(
        ...(optionPromotions[0].benefit?.gifts || []).map((item) => ({
          src: item.imageUrl,
          alt: item.name,
          sku: item.sku,
          quantity: item.quantity,
        })),
      );
    }
    let flashSale;
    let isFlashSale = false;
    const flSale = flashSales[0];
    if (flashSales.length) {
      isFlashSale = true;
      const availableToFlSale =
        product.totalAvailable === null
          ? flSale.totalCount - flSale.usedCount
          : Math.min(
              flSale.totalCount - flSale.usedCount,
              product.totalAvailable,
            );
      flashSale = cleanObject({
        endAt: parseInt(flSale.endTimestampSec) * 1000,
        text:
          availableToFlSale > 0
            ? t('Còn lại {{var0}}', {
                var0: availableToFlSale,
              })
            : t('Cháy hàng'),
        discountPercent:
          typeof productPrice?.discountPercent === 'number'
            ? Math.round(productPrice?.discountPercent)
            : undefined,
      });
    }
    const showContact =
      (typeof product.totalAvailable === 'number' &&
        product.totalAvailable <= 0) ||
      !isValidPrice(product) ||
      !product?.status?.sellable;

    let addToCart: AddToCartProps | undefined;
    let ctaButton: ProductCardButton | undefined;
    let contactButton: ProductCardButton | undefined;
    if (cartV2) {
      const currentOrderLine = cartV2.flattenItems.find(
        (item: CartItem) =>
          item.sku === productInfo.sku &&
          item.allowedUserActions?.includes(
            AllowedUserActionItem.CHANGE_QUANTITY,
          ),
      );
      const optionPromotions = promotions.filter(
        (el) =>
          !el.isDefault &&
          el.applyOn === PromotionApplyOnEnum.product &&
          el.type === 'product',
      );

      const quantityInCart =
        (currentOrderLine && currentOrderLine.quantity) || 0;

      addToCart = {
        onAddToCart: async (quantity = 1) => {
          const res = await cartV2.onAddSingleItem(
            {
              sku: productInfo.sku,
              sellerSku: productInfo.sellerSku,
              quantity: quantity,
              promotionId: optionPromotions[0]?.id,
            },
            true,
          );
          const { trackAddToCart, AddToCartType } = await import(
            '@teko/tracking'
          );

          const trackAddToCartParams: TrackAddToCartParams = {
            product: {
              sku: productInfo.sellerSku,
              name: productInfo.name,
              brand: productInfo.brand,
              categories: productInfo.categories,
              price: parseInt(prices?.[0].supplierRetailPrice),
              addToCartPrice: parseFloat(product.prices[0].latestPrice || ''),
              sellerSku: productInfo.sellerSku,
            },
            selectedPromotionId: optionPromotions[0]?.id,
            type: AddToCartType.ADDTOCART,
          };

          if (res) {
            trackAddToCart({
              ...trackAddToCartParams,
              status: 'success',
              cartId: res.id,
            });
          } else {
            trackAddToCart({
              ...trackAddToCartParams,
              status: 'failed',
            });
          }
        },
        onIncrease: async () => {
          if (currentOrderLine) {
            await cartV2.onIncrease(currentOrderLine);
          }
        },
        onDecrease: async () => {
          if (currentOrderLine) {
            if (quantityInCart <= 1) {
              await cartV2.onRemoveItem(currentOrderLine);
            } else {
              await cartV2.onDecrease(currentOrderLine);
            }
          }
        },
        onRemoveItem: async () => {
          if (currentOrderLine) {
            await cartV2.onRemoveItem(currentOrderLine);
          }
        },
        onChangeQuantity: async (newQuantity: number) => {
          if (currentOrderLine) {
            await cartV2.onChangeQuantity(currentOrderLine, newQuantity);
          }
        },
        quantityInCart,
      };
    }

    if (!showContact) {
      ctaButton = {
        children: t('Thêm vào giỏ'),
        variant: 'secondary',
        ...(isServerless()
          ? {
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                addToCart?.onAddToCart();
              },
            }
          : {}),
      };
    } else {
      if (contactLink)
        contactButton = {
          children: t('Liên hệ'),
          variant: 'secondary',
          ...(isServerless()
            ? {
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  window.open(contactLink);
                },
              }
            : {}),
        };
      else
        ctaButton = {
          children: t('Hết hàng'),
          variant: 'default',
          disabled: true,
        };
    }

    let titleTag: ProductTag | undefined;
    const isOutOfStock =
      typeof product.totalAvailable === 'number' && product.totalAvailable <= 0;
    if (isOutOfStock) {
      titleTag = {
        variant: 'link',
        content: contactLink ? t('Liên hệ đặt hàng') : t('Hết hàng'),
      };
    }
    const price = convertPrice(product);
    if (!price?.price) {
      titleTag = {
        variant: 'pending',
        content: t('Liên hệ'),
      };
    }
    const infoTags = [];
    if (status && status.sellingCode === 'hang_khac') {
      infoTags.push({
        content: t('Hàng quà tặng'),
        variant: 'default',
      } as TagProps);
    }
    const quantity =
      totalAvailable === null
        ? 1000
        : isFlashSale
        ? Math.min(flSale.totalCount - flSale.usedCount, totalAvailable)
        : totalAvailable;
    const { name, code } = productInfo?.brand || { name: '', code: '' };

    return {
      sku: productInfo.sku,
      sellerSku: productInfo.sellerSku,
      name: productInfo.displayName || productInfo.name,
      imageUrl: productInfo.imageUrl,
      price: productPrice
        ? getDisplayPrice(productPrice, isFlashSale)
        : undefined,
      stockQuantity: quantity,
      warranty: productInfo.warranty?.months
        ? {
            months: productInfo.warranty?.months,
          }
        : undefined,
      freeship,
      flashSale: flashSale,
      addToCart,
      frame: frames?.length
        ? getFrame(productInfo.tags, frames || [])
        : undefined,
      hasGift,
      titleTag,
      infoTags,
      link: {
        as: {
          pathname: `/${product?.productInfo?.canonical}`,
          query:
            product?.productInfo?.canonical &&
            isVariantUrlGrouped(product.productInfo.canonical)
              ? {
                  sku: productInfo.sku,
                }
              : {},
        },
        href: {
          pathname: PRODUCT_DETAIL_PAGE,
          query: { sku: productInfo.sku },
        },
        passHref: true,
      },
      brand: {
        name: name,
        code,
      },
      ctaButton: showCtaButton ? ctaButton : undefined,
      contactButton,
      ...(showUom && {
        uom: productInfo.uomName,
      }),
      ...pos365StockInfo,
      showContact:
        isOutOfStock || !isValidPrice(product) || !product?.status?.sellable,
      ...(gifts.length > 0 && {
        gifts: {
          label: t('QUÀ TẶNG'),
          images: gifts,
        },
      }),
      selectPromotionId,
      categories: productInfo?.categories || [],
      isAlcoholicCategory:
        product?.isAlcoholicCategory ||
        isAdultCategoryProduct(productInfo?.categories),
      showNewProductGift,
      attributeGroups: productDetail?.attributeGroups,
      wishlistButton,
      bestComboDiscount: bestComboDiscount || undefined,
      benefitLabel: benefitLabel || undefined,
    };
  };
}
export const productHelper = new ProductHelper();

export const getSearchVariables = ({
  query,
  pos365StoreCode,
  location,
  returnFilter = true,
}: {
  query: ObjectMap;
  pos365StoreCode?: string;
  location?: string;
  returnFilter?: boolean;
}) => {
  const defaultVariables: ObjectMap = {
    itemsPerPage: ITEM_PER_PAGE_IN_LISTING_PAGE,
    location,
    ...query,
    page: query.page || 1,
  };

  if (pos365StoreCode) {
    defaultVariables.pos365StoreCode = pos365StoreCode;
  }
  if (returnFilter) {
    defaultVariables.returnFilterable = [
      'FILTER_TYPE_BRAND',
      'FILTER_TYPE_PRICE',
      'FILTER_TYPE_ATTRIBUTE',
      'FILTER_TYPE_CLEARANCE',
    ];
  }

  return defaultVariables;
};

const convertPrice = (product: DiscoveryProduct): MobileProductPriceProps => {
  const { prices } = product;
  const price = parseInt(prices?.[0]?.latestPrice);
  const supplierRetailPrice = parseInt(prices?.[0]?.supplierRetailPrice);
  const listedPrice = price > supplierRetailPrice ? null : supplierRetailPrice;
  const discount = listedPrice
    ? getDiscountPercent(price, listedPrice)
    : undefined;
  return {
    price: isNaN(price) ? undefined : price,
    listedPrice: listedPrice ?? undefined,
    discount: discount,
  };
};

const getDiscountPercent = (finalPrice: number, oldPrice: number): number => {
  let discount = 0;
  if (oldPrice) {
    discount = 100 - Math.round((finalPrice * 100) / oldPrice);
  }
  return discount;
};

export class DiscoveryHelper {
  private _productHelper: ProductHelper;
  constructor(private readonly tenantConfig: TenantConfig) {
    this._productHelper = new ProductHelper(tenantConfig.freeShippingThreshold);
  }
  /**
   * @discovery_v2
   *- clean discovery search product data
   *- convert to product card props
   */
  cleanupDiscoveryV2SearchProduct(
    product: SearchProduct,
    configs?: OptionConvertProductCart,
    omitKeys?: Array<keyof ProductCardProps>,
  ): ProductCardProps {
    const { frames, cartV2, hideHighlightAttribute } = configs || {};
    const {
      isDisplayUom: showUom,
      isAllowATCFromListing: showCtaButton,
      contactLink,
      epicOmni1286,
      showNewProductGift,
      numberDisplayFormat,
      uomCodeToDisplayDecimalQuantity,
      epicOmni1550,
    } = this.tenantConfig;

    const getProductPrice = () => {
      const minLatestPrice = product.minLatestPrice
        ? parseFloat(product.minLatestPrice)
        : undefined;
      const maxLatestPrice = product.maxLatestPrice
        ? parseFloat(product.maxLatestPrice)
        : undefined;
      return cleanObject({
        latestPrice: Number(product.latestPrice) || undefined,
        supplierRetailPrice: parseInt(product.supplierRetailPrice || '0'),
        discountAmount:
          // not display save... incase there is config flash sale
          !parseInt(product.promotionEndTime || '0') && product.discountAmount
            ? parseInt(product.discountAmount)
            : undefined,
        discountPercent: product.discountPercent
          ? numberDisplayFormat
            ? `-${product.discountPercent}%`.replace(
                '.',
                numberDisplayFormat.decimalSeparator,
              )
            : `-${product.discountPercent}%`
          : '',
        ...(minLatestPrice &&
        maxLatestPrice &&
        minLatestPrice !== maxLatestPrice
          ? {
              priceRange: {
                min: minLatestPrice,
                max: maxLatestPrice,
              },
            }
          : {}),
      });
    };

    const price = getProductPrice();

    const getTitleTag = (): ProductCardProps['titleTag'] => {
      const isOutOfStock =
        typeof product.totalAvailable === 'number' &&
        product.totalAvailable <= 0;

      if (isOutOfStock) {
        return {
          variant: 'link',
          content: contactLink ? t('Liên hệ đặt hàng') : t('Hết hàng'),
        };
      }
      if (!price.latestPrice) {
        return { variant: 'pending', content: t('Liên hệ') };
      }
    };

    const quantity = product.totalAvailable ?? 1000;
    const showContact =
      (typeof product.totalAvailable === 'number' &&
        product.totalAvailable <= 0) ||
      !price.latestPrice ||
      !product.sellable;

    const getContactButton = (): ProductCardProps['contactButton'] => {
      if (cartV2 && contactLink && showContact)
        return {
          children: t('Liên hệ'),
          variant: 'secondary',
          ...(isServerless()
            ? {
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  window.open(contactLink);
                },
              }
            : {}),
        };
    };

    const result: ProductCardProps = {
      sku: product.sku || '',
      sellerSku: product.sellerSku || '',
      name: product.name || '',
      imageUrl: product.imageUrl || '',
      price: price as ProductCardProps['price'],
      stockQuantity: quantity,
      selectPromotionId: product.selectedPromotionId,
      freeship:
        typeof this._productHelper.milestoneShippingFee === 'number' &&
        this._productHelper.milestoneShippingFee >= 0 &&
        (price?.latestPrice || 0) > 0 &&
        Number(price?.latestPrice) >= this._productHelper.milestoneShippingFee,

      frame: frames?.length
        ? getFrame(product.tags || [], frames || [])
        : undefined,
      hasGift: (product.gifts?.length || 0) > 0,
      showNewProductGift,
      titleTag: showCtaButton ? undefined : getTitleTag(),

      link: {
        as: {
          pathname: `/${product?.canonical}`,
          query: isVariantUrlGrouped(product.canonical || '')
            ? {
                sku: product.sku,
              }
            : {},
        },
        href: {
          pathname: PRODUCT_DETAIL_PAGE,
          query: { sku: product.sku },
        },
        passHref: true,
      },
      brand: {
        name: product.brandName || '',
        code: product.brandCode?.toString() || '', // TODO
      },
      ...(showUom
        ? {
            uom: product.uomName,
          }
        : {}),
      contactButton: showCtaButton ? getContactButton() : undefined,
      gifts: product.gifts?.length
        ? {
            label: t('QUÀ TẶNG'),
            images: product.gifts?.map((gift) => ({
              src: gift.imageUrl || '',
              alt: gift.name || '',
              sku: '', // TODO
              quantity: gift.quantity,
            })),
          }
        : undefined,
      flashSale: parseInt(product.promotionEndTime || '0')
        ? cleanObject({
            endAt: parseInt(product.promotionEndTime || '0') * 1000,
            text:
              (product.totalAvailable || 0) > 0
                ? t('Còn lại {{var0}}', {
                    var0: product.totalAvailable,
                  })
                : t('Cháy hàng'),
            discountPercent: Math.round(product?.discountPercent || 0),
            endTimestampSec: product.promotionEndTime,
          })
        : undefined,
      isAlcoholicCategory: product.isAdult,
      showContact: showContact,
      bestComboDiscount: epicOmni1286 ? product.bestComboDiscount : undefined,
      benefitLabel: product.benefitLabel,
      numberDisplayFormat,
      highlight: hideHighlightAttribute ? undefined : product.highlight,
      precisionToDisplayDecimalQuantity:
        uomCodeToDisplayDecimalQuantity === product.uomCode ? 3 : 0,
      materials: product.materials,
      averageRating: epicOmni1550 ? product.averageRating : undefined,
    };
    const productResult = omit(result, omitKeys || []);
    return cleanObject(productResult as ProductCardProps);
  }
}

/**
 * don't use this function in swr fetcher
 * because cartState sometimes be updated
 */
export const createHandleEventAndCtaButton = (
  product: ProductCardProps,
  configs?: OptionConvertProductCart,
): ProductCardProps => {
  const {
    cartV2,
    buildPCV2,
    contactLink,
    showCtaButton,
    showCtaButtons,
    showCtaButtonsWithQuantity,
    isMobile = false,
    enableTrackOpenInNewTab = true,
    wishlistButton,
  } = configs || {};

  const getAddToCart = (): ProductCardProps['addToCart'] => {
    if (cartV2) {
      const currentOrderLine = cartV2.flattenItems.find(
        (item: CartItem) =>
          item.sku === product.sku &&
          item.allowedUserActions?.includes(
            AllowedUserActionItem.CHANGE_QUANTITY,
          ),
      );
      const quantityInCart =
        (currentOrderLine && currentOrderLine.quantity) || 0;
      if (!isServerless()) return;

      return {
        onAddToCart: async (quantity = 1) => {
          const res = await cartV2.onAddSingleItem(
            {
              sku: product.sku || '',
              sellerSku: product.sellerSku,
              quantity: quantity,
              promotionId:
                product.selectPromotionId !== 0
                  ? product.selectPromotionId
                  : undefined,
            },
            true,
          );
          const { trackAddToCart, AddToCartType } = await import(
            '@teko/tracking'
          );

          const trackAddToCartParams: TrackAddToCartParams = {
            product: {
              sku: product.sellerSku || '',
              name: product.name || '',
              price: product.price?.latestPrice as number,
              brand: {
                name: product.brand?.name || '',
              },
              addToCartPrice: product.price?.supplierRetailPrice || 0,
              sellerSku: product.sellerSku,
            },
            selectedPromotionId: product.selectPromotionId,
            type: AddToCartType.ADDTOCART,
            status: res ? 'success' : 'failed',
          };

          trackAddToCart({
            ...trackAddToCartParams,
            cartId: res ? res.id : undefined,
          });
        },
        onIncrease: async () => {
          if (currentOrderLine) {
            await cartV2.onIncrease(currentOrderLine);
          }
        },
        onDecrease: async () => {
          if (currentOrderLine) {
            if (quantityInCart <= 1) {
              await cartV2.onRemoveItem(currentOrderLine);
            } else {
              await cartV2.onDecrease(currentOrderLine);
            }
          }
        },
        onRemoveItem: async () => {
          if (currentOrderLine) {
            await cartV2.onRemoveItem(currentOrderLine);
          }
        },
        onChangeQuantity: async (newQuantity: number) => {
          if (currentOrderLine) {
            await cartV2.onChangeQuantity(currentOrderLine, newQuantity, false);
          }
        },
        quantityInCart,
      };
    }
  };
  const getCtaButton = (): ProductCardProps['ctaButton'] => {
    if (!product.showContact)
      return {
        children: t('Thêm vào giỏ'),
        variant: 'secondary',
        ...(isServerless()
          ? {
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                addToCart?.onAddToCart();
              },
            }
          : {}),
      };
    else {
      if (contactLink)
        return {
          children: t('Liên hệ'),
          variant: 'secondary',
          ...(isServerless()
            ? {
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  window.open(contactLink);
                },
              }
            : {}),
        };
      else
        return {
          children: t('Hết hàng'),
          variant: 'default',
          disabled: true,
        };
    }
  };

  const getCtaButtons = (): ProductCardProps['ctaButtons'] => {
    const ctaButtons: ProductCardButton[] = [];
    const quantityInCart = getAddToCart()?.quantityInCart;

    if (!product.showContact) {
      ctaButtons.push(
        {
          children: isMobile ? (
            <Button
              variant="secondary"
              size="s"
              block
              padding="s0"
              onClick={() => buildPCV2?.onUpdateConfig(product)}
              width="100%"
            >
              {t('Sửa cấu hình')}
            </Button>
          ) : (
            <Tooltip title={t('Sửa cấu hình')} placement="top">
              <Button
                variant="secondary"
                size="s"
                block
                padding="s0"
                onClick={() => buildPCV2?.onUpdateConfig(product)}
                maxWidth="40px"
              >
                <EditOutlined
                  style={{ minWidth: 'max-content', margin: '0 1.25rem' }}
                />
              </Button>
            </Tooltip>
          ),
        },
        {
          children:
            (quantityInCart ?? 0) === 0 ? (
              <Button
                variant="primary"
                size="s"
                block
                padding="s0"
                onClick={(
                  e: React.MouseEvent<HTMLButtonElement> &
                    React.MouseEvent<HTMLAnchorElement>,
                ) => {
                  e.preventDefault();
                  addToCart?.onAddToCart();
                }}
              >
                <Caption level={2} line={1}>
                  {t('Thêm vào giỏ')}
                </Caption>
              </Button>
            ) : null,
        },
      );
    } else {
      if (contactLink)
        ctaButtons.push({
          children: t('Liên hệ'),
          variant: 'secondary',
          ...(isServerless()
            ? {
                onClick: (
                  e: React.MouseEvent<HTMLButtonElement> &
                    React.MouseEvent<HTMLAnchorElement>,
                ) => {
                  e.preventDefault();
                  window.open(contactLink);
                },
              }
            : {}),
        });
      else
        ctaButtons.push({
          children: t('Hết hàng'),
          variant: 'default',
          disabled: true,
        });
    }
    return ctaButtons;
  };

  const addToCart =
    showCtaButton || showCtaButtons ? getAddToCart() : undefined;
  const ctaButton = showCtaButton ? getCtaButton() : undefined;
  const ctaButtons = showCtaButtons ? getCtaButtons() : undefined;
  const contactButton = showCtaButton ? product.contactButton : undefined;
  const onWrapperAuxClick: ProductCardProps['onWrapperAuxClick'] =
    !!enableTrackOpenInNewTab && !isMobile
      ? (event) => {
          // check if button click is middle mouse button
          if (event.button === 1) {
            trackCustomEvent('openInNewTab', {
              action: 'middleClick',
              property: 'productCard',
            });
          }
        }
      : undefined;
  const onWrapperClick: ProductCardProps['onWrapperClick'] =
    !!enableTrackOpenInNewTab && !isMobile
      ? (event) => {
          // check if button click is left mouse button & ctrl key (Windows or Linux) or cmd key (Mac) is pressed
          if ((!!event.ctrlKey || !!event.metaKey) && event.buttons === 0) {
            trackCustomEvent('openInNewTab', {
              action: !!event.ctrlKey ? 'ctrlClick' : 'cmdClick',
              property: 'productCard',
            });
          }
        }
      : undefined;
  return {
    ...product,
    ctaButton,
    ctaButtons,
    wishlistButton,
    addToCart,
    contactButton,
    showCtaButtonsWithQuantity,
    onWrapperAuxClick,
    onWrapperClick,
  };
};

// helpers for search page
export const convertDiscoveryFilter = (
  filters?: SearchProductFilter,
  displayFilterTypeCategory?: boolean,
) => {
  if (!filters) return [];
  const convertedFilters: Filter[] = [];
  // brands
  if (filters.brands?.length) {
    convertedFilters.push({
      code: 'brands',
      name: 'Thương hiệu',
      options: filters.brands.map((brand) => ({
        filterId: null,
        filterCode: 'brands',
        optionId: brand.code || null,
        optionName: brand.name || '',
        selected: brand.selected || false,
      })),
    });
  }
  // attributes
  filters.attributes?.forEach((attribute) => {
    if (attribute.values?.length) {
      convertedFilters.push({
        id: `attributes.id.${attribute.id}`,
        code: `attributes.${attribute.code}`,
        name: attribute.name || '',
        options: attribute.values.map((value) => ({
          filterId:
            attribute.id !== undefined ? `attributes.id.${attribute.id}` : null,
          filterCode: attribute.code ? `attributes.${attribute.code}` : null,
          optionId: value.optionId || null,
          optionName: value.value || '',
          selected: value.selected || false,
        })),
      });
    }
  });
  // clearanceTypes
  if (filters?.clearanceTypes?.length) {
    convertedFilters.push({
      code: 'clearanceTypeIds',
      name: 'Tình trạng sản phẩm',
      options: filters.clearanceTypes.map((clearanceType) => ({
        filterId: null,
        filterCode: 'clearanceTypeIds',
        optionId: clearanceType.id === undefined ? null : clearanceType.id,
        optionName: clearanceType.name || '',
        selected: clearanceType.selected || false,
      })),
    });
  }
  // masterCategories
  if (filters?.masterCategories?.length && !displayFilterTypeCategory) {
    convertedFilters.push({
      code: 'masterCategoryIds',
      name: 'Danh mục sản phẩm',
      options: filters.masterCategories.map((category) => ({
        filterId: null,
        filterCode: 'masterCategoryIds',
        optionId:
          category.masterCategoryId === undefined
            ? null
            : category.masterCategoryId,
        optionName: category.sellerCategoryName || '',
        selected: category.selected || false,
      })),
    });
  }
  return convertedFilters;
};

export const convertToPayloadFilter = (filterParams: ObjectMap) => {
  const result: { [key: string]: unknown } = {};
  const attributes: { id?: number; code?: string; optionIds: string[] }[] = [];
  const legacyListingToSearchMapping = new Map([
    ['price_lte', 'priceLte'],
    ['price_gte', 'priceGte'],
    ['sellerCategories', 'masterCategoryIds'],
  ]);

  // alter old listing fields from filterParams by new search fields
  legacyListingToSearchMapping.forEach((searchField, listingField) => {
    if (Object.hasOwn(filterParams, listingField)) {
      Object.assign(filterParams, {
        [searchField]: filterParams[listingField],
      });
      delete filterParams[listingField];
    }
  });

  Object.keys(filterParams).forEach((key) => {
    // attributes
    if (key.startsWith('attributes.')) {
      if (key.startsWith('attributes.id.')) {
        attributes.push({
          id: parseInt(key.replace('attributes.id.', '')),
          optionIds: filterParams[key].split(','),
        });
      } else {
        attributes.push({
          code: key.replace('attributes.', ''),
          optionIds: filterParams[key].split(','),
        });
      }
    }
    // boolean value
    else if (PRIMITIVE_PAYLOAD_FILTER.includes(key)) {
      result[key] = filterParams[key];
    }
    // array of number value
    else if (ARRAY_OF_NUMBER_PAYLOAD_FILTER.includes(key)) {
      result[key] =
        typeof filterParams[key] === 'string'
          ? filterParams[key].split(',').map((el: string | number) => +el)
          : filterParams[key].map((el: string | number) => +el);
    }
    // array of string value
    else if (ARRAY_OF_STRING_PAYLOAD_FILTER.includes(key)) {
      result[key] =
        typeof filterParams[key] === 'string'
          ? filterParams[key].split(',')
          : filterParams[key];
    }
  });
  if (filterParams['attributes'] || attributes.length) {
    result['attributes'] = [
      ...(filterParams['attributes'] || []),
      ...attributes,
    ];
  }

  return result;
};

export const convertToPayloadSorting = (sort?: string, order?: string) => {
  return {
    sort: (sort as SortBy) ?? 'SORT_BY_UNSPECIFIED',
    order:
      order && ORDER_PAYLOAD_SORTING[order]
        ? (ORDER_PAYLOAD_SORTING[order] as OrderBy)
        : 'ORDER_BY_UNSPECIFIED',
  };
};

export const buildTreeCategoriesFilter = (
  categories: Array<CategoriesFilter>,
) => {
  const rootCategories = categories.filter(({ parentId }) => !parentId);
  const buildChildren = (
    root: CategoriesFilter | CategoriesFilterOption,
  ): CategoriesFilterOption => {
    const children = categories.filter(({ parentId }) => parentId === root.id);
    return {
      ...root,
      children: children.map(buildChildren),
    } as CategoriesFilterOption;
  };

  return rootCategories.map((root) => buildChildren(root));
};
